import moment from "moment";
import { OrderDTO, OrderProduct } from "../pages/NewOrder/interfaces";
import { createContext, useState, ReactNode } from "react";
// Define the shape of the context, including the order and the updateOrder function
interface OrderContextType {
    order: OrderDTO;
    tmpOldOrder: OrderDTO;
    isUpdateOrder: boolean;
    updatingOrderId: string;
    updateOrder: (
        updatedFields: Partial<OrderDTO>,
        saveTmpOldOrderObj?: boolean
    ) => void;
    restartIsUpdateOrder: (storeId: string) => void;
    onStartUpdateOrder: (orderId: string, storeId: string) => void;
    updatingProductModal: boolean;
    setUpdatingProductModal: (modal: boolean) => void;
    updatingProduct: OrderProduct | undefined;
    setUpdatingProduct: (product: OrderProduct | undefined) => void;
    updatingQuantityProduct: OrderProduct | undefined;
    setUpdatingQuantityProduct: (product: OrderProduct | undefined) => void;
    updatingQuantityProductModal: boolean;
    setUpdatingQuantityProductModal: (modal: boolean) => void;
    setEditProductPriceModal: (modal: boolean) => void;
    editProductPriceModal: boolean;
    updatingProductPrice: OrderProduct | undefined;
    setUpdatingProductPrice: (product: OrderProduct | undefined) => void;
    isUpdatingOrder: boolean;
    setIsUpdatingOrder: (isUpdatingOrder: boolean) => void;
    currentUpdatingOrder: any;
    setCurrentUpdatingOrder: (currentUpdatingOrder: any) => void;
    lastAddedOrderProduct: OrderProduct | null;
    setLastAddedOrderProduct: (product: OrderProduct | null) => void;
}

// Create the default context value
const defaultOrder: OrderDTO = {
    sequence: "",
    delivery: false,
    pickup: false,
    express: false,
    deliveryInfo: {
        fromTime: moment().startOf("day").toDate(),
        toTime: moment().endOf("day").toDate(),
        date: new Date(),
    },
    pickupInfo: undefined,
    phone: "",
    email: "",
    address: undefined,
    notes: undefined,
    customer: "",
    status: "",
    store: "",
    paymentMethod: "",
    prepaidAmount: 0,
    prepaidPaymentMethod: "",
    discountsToApply: undefined,
    products: [],
    buyAndGetProducts: [],
    deliveryRoute: undefined,
    pickupRoute: undefined,
    cashierBox: "",
    createdDate: new Date(),
    total: 0,
    discountCodeObj: undefined,
    discountCode: "",
    customerInfo: undefined,
    notifyBy: "none",
};

// Create the context with default values
export const OrderContext = createContext<OrderContextType>({
    order: defaultOrder,
    tmpOldOrder: defaultOrder,
    updateOrder: () => { },
    isUpdateOrder: false,
    updatingOrderId: "",
    restartIsUpdateOrder: (storeId: string) => { },
    onStartUpdateOrder: (orderId: string, storeId: string) => { },
    updatingProductModal: false,
    setUpdatingProductModal: () => { },
    updatingProduct: undefined,
    setUpdatingProduct: (product: OrderProduct | undefined) => { },
    updatingQuantityProductModal: false,
    setUpdatingQuantityProductModal: (value: boolean) => { },
    updatingQuantityProduct: undefined,
    setUpdatingQuantityProduct: (product: OrderProduct | undefined) => { },
    editProductPriceModal: false,
    setEditProductPriceModal: () => { },
    updatingProductPrice: undefined,
    setUpdatingProductPrice: (product: OrderProduct | undefined) => { },
    isUpdatingOrder: false,
    setIsUpdatingOrder: (isUpdatingOrder: boolean) => { },
    currentUpdatingOrder: undefined,
    setCurrentUpdatingOrder: (currentUpdatingOrder: any) => { },
    lastAddedOrderProduct: null,
    setLastAddedOrderProduct: (product: OrderProduct | null) => { },
});

// Define a provider component
export const OrderProvider = ({ children }: { children: ReactNode }) => {
    const [isUpdatingOrder, setIsUpdatingOrder] = useState<boolean>(false);
    const [updatingProductModal, setUpdatingProductModal] = useState<boolean>(false);
    const [updatingProduct, setUpdatingProduct] = useState<OrderProduct | undefined>(undefined);
    const [updatingQuantityProduct, setUpdatingQuantityProduct] = useState<OrderProduct | undefined>(undefined);
    const [updatingQuantityProductModal, setUpdatingQuantityProductModal] = useState<boolean>(false);
    const [editProductPriceModal, setEditProductPriceModal] = useState<boolean>(false);
    const [updatingProductPrice, setUpdatingProductPrice] = useState<OrderProduct | undefined>(undefined);
    const [order, setOrder] = useState<OrderDTO>(defaultOrder);
    const [tmpOldOrder, setTmpOldOrder] = useState<OrderDTO>(defaultOrder);
    const [lastAddedOrderProduct, setLastAddedOrderProduct] = useState<OrderProduct | null>(null);
    const [updatingOrderId, setUpdatingOrderId] = useState({
        orderId: "",
        isUpdateOrder: false,
    });
    const [currentUpdatingOrder, setCurrentUpdatingOrder] = useState<any>(undefined);
    // Function to update the order state, supporting nested field updates
    const updateOrder = (
        updatedFields: Partial<OrderDTO>,
        saveTmpOldOrderObj = false
    ) => {
        setOrder((prevOrder) => {
            let pickupInfo: any = {
                ...(prevOrder.pickupInfo || {}),
                ...(updatedFields.pickupInfo || {}),
            };

            if (!Object.keys(pickupInfo).length) pickupInfo = undefined;
            return {
                ...prevOrder,
                ...updatedFields,
                products: updatedFields.products
                    ? [...updatedFields.products]
                    : prevOrder.products,
                pickupInfo: pickupInfo,
                deliveryInfo: {
                    ...(prevOrder.deliveryInfo || {}),
                    ...(updatedFields.deliveryInfo || {}),
                },
            };
        });
        if (saveTmpOldOrderObj) {
            const pickupInfo = updatedFields.pickupInfo || undefined;

            setTmpOldOrder((prevOrder) => ({
                ...prevOrder,
                ...updatedFields,
                products: (updatedFields.products || prevOrder.products || []).map((prod) => prod),
                pickupInfo: pickupInfo,
                deliveryInfo: {
                    ...(prevOrder.deliveryInfo || {}),
                    ...(updatedFields.deliveryInfo || {}),
                },
                cashierBox: "", //cashierbox is not updatable field (this is used at the order create because we can create payment lines at that moment)
            }));
        }
    };

    const restartIsUpdateOrder = async (storeId: string) => {
        setLastAddedOrderProduct(null);
        setUpdatingOrderId({
            orderId: "",
            isUpdateOrder: false,
        });
    };

    const onStartUpdateOrder = (orderId: string, storeId: string) => {
        try {
            setLastAddedOrderProduct(null);
            setUpdatingOrderId({
                orderId,
                isUpdateOrder: true,
            });
        } catch (e) {
            console.log("[ERROR] onStartUpdateOrder", e);
        }
    };

    return (
        <OrderContext.Provider
            value={{
                tmpOldOrder,
                order,
                updateOrder,
                isUpdateOrder: updatingOrderId.isUpdateOrder,
                updatingOrderId: updatingOrderId.orderId,
                restartIsUpdateOrder,
                onStartUpdateOrder,
                updatingProductModal,
                setUpdatingProductModal,
                updatingProduct,
                setUpdatingProduct,
                updatingQuantityProduct,
                setUpdatingQuantityProduct,
                updatingQuantityProductModal,
                setUpdatingQuantityProductModal,
                editProductPriceModal,
                setEditProductPriceModal,
                updatingProductPrice,
                setUpdatingProductPrice,
                isUpdatingOrder,
                setIsUpdatingOrder,
                currentUpdatingOrder,
                setCurrentUpdatingOrder,
                lastAddedOrderProduct,
                setLastAddedOrderProduct,
            }}
        >
            {children}
        </OrderContext.Provider>
    );
};
